import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import palette from '../../shared/theme/palette';
import Panel from '../../components/Panel';
import StatusChip from '../../components/StatusChip';
import ViewAllDialog from '../../components/ViewAllDialog';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/service/dataprovider';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function LeaseRequests({ lease }) {
  const { t } = useTranslation();
  const [showViewAll, setShowViewAll] = useState(false);

  const { isLoading, error, data } = useQuery({
    queryKey: [`workOrders/${lease.id}`],
    queryFn: () => api.get(`/v1/admin/work-orders`, { params: { leaseId: lease.id } }),
  });
  const workOrders = data?.data;
  if (isLoading) {
    return (
      <Panel sx={{ textAlign: 'center' }} padding={6}>
        <CircularProgress color="primary" />
      </Panel>
    );
  }
  if (error) {
    return (
      <Typography variant="h6" sx={{ color: palette.error[50] }}>
        {error?.response?.data?.message ?? `There is an error for loading work orders for lease ${lease.id}. Try again later`}
      </Typography>
    );
  }
  if (!workOrders) return null;

  const firstWorkOrders = workOrders.slice(0, 5);
  return (
    <Panel padding={0} sx={{ p: 2, pb: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: palette.grey[50] }}>{t('lease.request.title')}</Typography>
      </Stack>
      {workOrders?.length > 0 ? (
        <Stack gap={3} sx={{ mb: 2 }}>
          {firstWorkOrders.map((workOrder, i) => (
            <WorkOrderItem key={`w-${i}`} workOrder={workOrder} />
          ))}
        </Stack>
      ) : (
        <Typography>{t('lease.request.empty')}</Typography>
      )}
      <Button disabled={!workOrders?.length} fullWidth variant="text" onClick={() => setShowViewAll(true)} sx={{ fontWeight: 500 }}>
        {t('lease.transaction.viewAll')}
      </Button>
      {showViewAll && <ViewAllModal workOrders={workOrders} onClose={() => setShowViewAll(false)} />}
    </Panel>
  );
}

function ViewAllModal({ workOrders, onClose }) {
  const { t } = useTranslation();

  return (
    <ViewAllDialog open onClose={onClose} title={t('lease.request.title')}>
      <Stack gap={3} sx={{ pt: 1 }}>
        {workOrders.map((workOrder, i) => (
          <WorkOrderItem key={`w-${i}`} workOrder={workOrder} />
        ))}
      </Stack>
    </ViewAllDialog>
  );
}

function WorkOrderItem({ workOrder }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography color="grey.800" sx={{ fontWeight: 600 }}>
          {workOrder.title}
        </Typography>
        <Typography variant="caption" color="grey.500">
          {moment(workOrder.ReportedDate).format('MM/DD/YY')}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography color="grey.800" sx={{ fontWeight: 600 }}>
          {`#${workOrder.Number}`}
        </Typography>
        <StatusChip status={workOrder.Status} size="small" sx={{ mt: 0.5 }} />
      </Box>
    </Stack>
  );
}
